import { usePage } from "@inertiajs/react"
import { PlayCircle } from "lucide-react"
import type { PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

interface Props {
  content: {
    title: string
    p1: string
    p2: string
    /** Placed on the section in the admin; the band is text-only without one. */
    photo?: { id: number; url: string | null; card_url?: string | null; alt_text: string | null } | null
    /** A location's tour, when the section's "Video tour" setting names one.
     *  The photo above is the still it rests on. */
    video?: {
      title: string
      embed_src: string | null
      file_url: string | null
      poster_url?: string | null
    } | null
  }
}

export default function HomeBuildingHistory({ content }: Props) {
  const { translations } = usePage<PageProps>().props
  const tc = translations.sanctuary_pages.common

  const video = content.video
  const hasVideo = !!video && !!(video.embed_src || video.file_url)
  // The photograph placed on this section is the still, so the frame someone
  // sees before pressing play is the building itself rather than whatever the
  // film happens to open on. The video library's own poster stands in if this
  // band has no photo of its own.
  const poster = content.photo?.url ?? video?.poster_url ?? null

  return (
    <section className="relative overflow-hidden bg-stone py-20">
      <BrandMotif variant="disc" className="absolute -top-28 -left-36" />
      <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
        {/* Without a tour the photograph opens the band. With one it is the
            still the film rests on, so the heading comes first and the
            invitation to watch arrives with the player. */}
        {!hasVideo && content.photo?.url && (
          <img
            src={content.photo.url}
            alt={content.photo.alt_text ?? ""}
            loading="lazy"
            className="mx-auto mb-10 aspect-[16/9] w-full rounded-2xl object-cover shadow-sm ring-1 ring-hairline"
          />
        )}
        <p className="mb-2 text-sm font-medium uppercase tracking-[0.2em] text-ink-muted">
          {tc.est_1836}
        </p>
        <h2>{content.title}</h2>

        {hasVideo && (
          <div className="mt-8">
            <p className="mb-3 flex items-center justify-center gap-2 text-sm font-medium uppercase tracking-[0.15em] text-ink-muted">
              <PlayCircle aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              {tc.take_a_tour}
            </p>
            <div className="aspect-video overflow-hidden rounded-2xl bg-stone-mid shadow-sm ring-1 ring-hairline">
              {video!.embed_src ? (
                <iframe
                  src={video!.embed_src}
                  title={video!.title}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <video
                  // With no still to rest on, #t=0.1 asks the browser to seek a
                  // fraction in so it paints a frame rather than a black box.
                  // With one, no fragment: the seek would replace the chosen
                  // still with whatever the film happens to open on.
                  src={poster ? video!.file_url! : `${video!.file_url!}#t=0.1`}
                  poster={poster ?? undefined}
                  title={video!.title}
                  controls
                  preload="metadata"
                  // object-cover so a still that isn't 16:9 fills the frame
                  // rather than sitting in bars.
                  className="h-full w-full object-cover"
                />
              )}
            </div>
          </div>
        )}

        <p className="mt-5 text-ink-muted">{content.p1}</p>
        <p className="mt-4 text-ink-muted">{content.p2}</p>
      </div>
    </section>
  )
}
